<template>
  <el-dialog v-model="visible" :title="title" width="90%" @close="close">
    <el-tabs v-model="activeTab">
      <el-tab-pane v-if="editable" lazy label="Scans" name="scans">
        <MalwareTable :filter="filter" :with-options="withScanOptions" />
      </el-tab-pane>
      <el-tab-pane lazy label="Alerts" name="alerts">
        <AlertsTab
          :sourceable-type="type"
          :sourceable-id="id"
          :active="activeTab === 'alerts'"
          :catalog-source="editable ? 'scan' : 'display'"
          @status="onAlertsStatus"
        />
      </el-tab-pane>
      <el-tab-pane v-if="editable" lazy name="custom">
        <template #label>
          <span class="custom-tab-label">
            Custom
            <span
              v-if="customPresent"
              class="tab-warning"
              title="Custom alerts are set"
              >!</span
            >
          </span>
        </template>
        <CustomAlertsTab
          :sourceable-type="type"
          :sourceable-id="id"
          :active="activeTab === 'custom'"
          @updated="customPresent = $event"
        />
      </el-tab-pane>
    </el-tabs>
  </el-dialog>
</template>
<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue'
import MalwareTable from './Table.vue'
import AlertsTab from './AlertsTab.vue'
import CustomAlertsTab from './CustomAlertsTab.vue'
import { useUserStore } from '@/store/modules/user'
import { fetchScopes } from '@/api/ad_scans'

export default defineComponent({
  name: 'MalwareModal',
  components: { MalwareTable, AlertsTab, CustomAlertsTab },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    id: {
      type: String
    },
    type: {
      type: String
    },
    name: {
      type: String
    }
  },
  setup(props, context) {
    const userStore = useUserStore()
    const activeTab = ref(userStore.isStaff ? 'scans' : 'alerts')
    const customPresent = ref(false)
    const editable = computed(() => userStore.isStaff)

    const close = () => {
      context.emit('update:modelValue', false)
      context.emit('close')
    }

    const filter = computed(() => {
      return {
        sourceable_type: props.type,
        sourceable_id: props.id
      }
    })

    const withScanOptions = computed(() => {
      return props.type === 'Feed'
    })

    const visible = computed(() => {
      return props.modelValue
    })

    const title = computed(() => {
      return `Malware scans for ${props.type} ${props.name} ID: ${props.id}`
    })

    const defaultTab = () => (editable.value ? 'scans' : 'alerts')

    const loadCustomPresent = async () => {
      if (!props.id || !props.type) return

      try {
        const result = await fetchScopes({
          sourceable_type: props.type,
          sourceable_id: props.id
        })
        customPresent.value = (result.scopes ?? []).some(
          (scope) => scope.count > 0
        )
      } catch (e) {
        console.error('fetchScopes error:', e)
      }
    }

    const onAlertsStatus = (present: boolean) => {
      customPresent.value = present
    }

    watch(
      () => [visible.value, props.id, props.type] as const,
      ([isVisible, id, type]) => {
        if (!isVisible || !id || !type) return

        activeTab.value = defaultTab()
        if (editable.value) loadCustomPresent()
      }
    )

    return {
      visible,
      close,
      filter,
      title,
      withScanOptions,
      activeTab,
      editable,
      customPresent,
      onAlertsStatus
    }
  }
})
</script>

<style scoped>
.custom-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.tab-warning {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #e6a23c;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}
</style>
